import { useRouter } from "next/router";
import PropTypes from "prop-types";

import AssignmentEditor from "@/assignments/AssignmentEditor";
import useAssignmentGet from "@/assignments/useAssignmentGet";
import NewselaGlobalLayout from "@/base";
import Footer from "@/base/Footer";
import RootProviders from "@/base/RootProviders";
import NotAllowedError from "@/errors/NotAllowedError";
import NotLoggedInError from "@/errors/NotLoggedInError";
import getUserProperties from "@/users/getUserProperties";
import useUser from "@/users/useUser";
import { getServerSideRedirectProps } from "@/utils/legacyAssignmentEditorUtils";

const propTypes = {
  /**
   * Pass an id for use with the content-document api
   */
  assignmentEmbedContentId: PropTypes.string,
};

const AssignmentCreatePage = ({ assignmentEmbedContentId }) => {
  const { data: user } = useUser();
  const { isStudent, isParent, isAuthenticatedUser, isSchoolEducator } =
    getUserProperties(user);
  const {
    query: {
      contentIds,
      copiedAssignmentId,
      createdFrom,
      action,
      courseId,
      courseTitle,
      provider,
    },
  } = useRouter();

  const { data: assignment } = useAssignmentGet(copiedAssignmentId, {
    include_template: true,
    with_archived_classrooms: !isStudent,
  });

  // Don't allow unauthenticated users to view content
  if (!user || !isAuthenticatedUser) {
    return <NewselaGlobalLayout mainContents={<NotLoggedInError />} />;
  }

  // Users must be either an educator or a parent to view content
  if (!isSchoolEducator && !isParent) {
    return <NewselaGlobalLayout mainContents={<NotAllowedError />} />;
  }

  const externalAssignmentContext = {
    action,
    courseId,
    courseTitle,
    provider,
  };
  return (
    <NewselaGlobalLayout
      mainContents={
        <>
          <AssignmentEditor
            actionName="Page View"
            trackClick={false}
            assignment={assignment}
            // If we have copiedAssignmentId in our url params,
            // Then we're creating a new assignment from a copy of an existing assignment.
            copiedAssignmentId={copiedAssignmentId}
            // createdFrom contains the contentId of content that we created an assignment from.
            createdFrom={createdFrom}
            // If corresponding content exists in Alexandria, pass the Langston content id here:
            assignmentEmbedContentId={assignmentEmbedContentId}
            // contentIds contains the contentIds found in the createdFrom's members.
            contentIds={contentIds?.split(",")}
            // externalAssignmentContext is necessary when creating an assignment from canvas
            externalAssignmentContext={externalAssignmentContext}
            streamProperties={{
              content_type: "assignment",
              description: "Assignment create page.",
            }}
          />
          <Footer />
        </>
      }
    />
  );
};

AssignmentCreatePage.propTypes = propTypes;

export default AssignmentCreatePage;

export async function getServerSideProps(ctx) {
  const RootProvidersInitialProps = await RootProviders.__getInitialProps({
    ctx,
  });
  const redirectProps = await getServerSideRedirectProps(ctx);
  return {
    props: {
      [RootProviders.InitialPropsName]: RootProvidersInitialProps,
    },
    ...redirectProps,
  };
}
